import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useLadderPrices, usePayment, useUser, useUserVip } from "@/stores/appStore";
import { PriceBadge } from "../site/PriceBadge";
import { TokenIcon } from "../fun-ui/icons";
import { BadgeType } from "@/stores/slices/commonSlice";



type PaymentDialogRechargeProps = {
  subtitle?: string | React.JSX.Element;
  scale: number;
  activedIndex?: number;
  onActivedIndexChange?: (index: number) => void;
  onPaymentOptionChange: (option: { renewalDays: number, paymentPrice: number }) => void;
}
export function PaymentDialogRecharge({
  subtitle: propSubtitle,
  scale = 1,
  activedIndex: propActivedIndex,
  onActivedIndexChange,
  onPaymentOptionChange,
}: PaymentDialogRechargeProps) {
  const [localActivedIndex, setLocalActivedIndex] = useState<number>();

  const activedIndex = propActivedIndex !== undefined ? propActivedIndex : localActivedIndex;

  const setActivedIndex = useCallback((index: number) => {
    if (onActivedIndexChange) {
      onActivedIndexChange(index);
    } else {
      setLocalActivedIndex(index);
    }
  }, [onActivedIndexChange]);

  const {
    paymentOpen,
    setPaymentPrice,
    getPaymentPrice,
  } = usePayment();

  const {
    vip: isVip,
  } = useUserVip();

  const {
    value,
    fetch,
  } = useLadderPrices().ladderPrices;

  useEffect(() => {
    if (!value || value.length === 0) {
      fetch();
    }
  }, [fetch, value]);

  const prices = useMemo(() => {
    if (!value) return [];

    // 根据 isVip 确定当前用户类型
    // isVip === 0 对应 'normal'，isVip === 1 对应 'plus'
    const currentUserType = isVip === 1 ? 'plus' : 'normal';

    return value.filter(({ price, stage, users }) => {
      // 过滤条件：价格大于0、包含pc场景、且users数组包含当前用户类型
      return price > 0 && stage.includes('pc') && users?.includes(currentUserType);
    });
  }, [value, isVip]);

  const paymentPrice = getPaymentPrice();
  const paymentPriceRef = useRef(paymentPrice);
  const isUpdatingFromPriceRef = useRef(false);
  const hasCalledOnPaymentOptionChangeRef = useRef(false);
  const isUserClickingRef = useRef(false);

  useEffect(() => {
    // 如果用户正在点击切换，不要根据 paymentPrice 重置 activedIndex
    if (isUserClickingRef.current) {
      isUserClickingRef.current = false;
      return;
    }
    if (paymentPrice !== 0 && prices.length > 0) {
      const index = prices.findIndex(({ price }) => price === paymentPrice);
      if (index !== -1) {
        if (index !== (activedIndex ?? localActivedIndex)) {
          isUpdatingFromPriceRef.current = true;
          hasCalledOnPaymentOptionChangeRef.current = false;
          setActivedIndex(index);
        } else if (!hasCalledOnPaymentOptionChangeRef.current) {
          hasCalledOnPaymentOptionChangeRef.current = true;
          onPaymentOptionChange({ renewalDays: 0, paymentPrice });
        }
      }
    }
  }, [paymentPrice, prices, setActivedIndex, activedIndex, localActivedIndex, onPaymentOptionChange]);

  useEffect(() => {
    paymentPriceRef.current = paymentPrice;
  }, [paymentPrice]);

  useEffect(() => {
    if (typeof activedIndex === 'undefined') {
      return;
    }
    const price = prices[activedIndex]?.price;
    if (price && price !== 0) {
      if (isUpdatingFromPriceRef.current) {
        isUpdatingFromPriceRef.current = false;
        hasCalledOnPaymentOptionChangeRef.current = true;
        onPaymentOptionChange({ renewalDays: 0, paymentPrice: price });
        return;
      }

      if (paymentPriceRef.current !== price) {
        onPaymentOptionChange({ renewalDays: 0, paymentPrice: price });
        setPaymentPrice(price);
      }
    }
  }, [prices, activedIndex, setPaymentPrice, onPaymentOptionChange, setActivedIndex]);

  const {
    userQuotas,
    balanceInsufficient: payment,
  } = useUser();
  let activedPrice = 0;
  let subtitle = <>你的飞豆余额不足，立即充值继续享用 AI 能力，加速论文读写</>;
  if (typeof payment === 'number' && (userQuotas?.balance || userQuotas?.balance === 0)) {
    if (payment === 0) {
      // 如果 payment 为 0，表示主动打开充值弹窗
      // 如果余额也为 0，显示余额不足的默认文案
      // 如果余额大于 0，显示当前余额信息
      if (userQuotas.balance === 0) {
        // 保持默认的余额不足文案
        subtitle = <>你的飞豆余额不足，立即充值继续享用 AI 能力，加速论文读写</>;
      } else {
        subtitle = <>当前剩 <span>{userQuotas.balance}</span> 飞豆，您可选择合适的飞豆充值数</>;
      }
    } else {
      activedPrice = payment - userQuotas.balance;
      subtitle = <>当前剩 <span>{userQuotas.balance}</span> 飞豆，还需 {activedPrice} 飞豆，请选择合适的飞豆充值数</>;
    }
  }

  useEffect(() => {
    if (paymentOpen === true && paymentPrice === 0) {
      setActivedIndex(0);
    }
  }, [paymentPrice, paymentOpen, setActivedIndex])

  return (
    <>
      <div
        className="text-muted-foreground text-center"
        style={{
          fontSize: `${16 * scale}px`,
          lineHeight: `${24 * scale}px`,
        }}
      >
        {subtitle}
      </div>
      <div
        style={{
          padding: `${40 * scale}px ${60 * scale}px ${28}px`,
          width: `${1154 * scale * 0.65}px`,
          height: `${472 * scale}px`,
          overflow: "hidden"
        }}
      >
        <div
          className="flex flex-col"
          style={{
            gap: `${16 * scale}px`,
          }}
        >
          {
            prices.map(({ price, vip, ...base }, index) => {
              const {
                coins, label, desc, badge
              } = (isVip ? vip : base);
              const {
                coins: extraCoins,
                label: extraLabel,
              } = (isVip ? base : vip);
              return (
                <div
                  key={`${price}/${coins}`}
                  onClick={() => {
                    isUserClickingRef.current = true;
                    setActivedIndex(index);
                  }}
                  className={cn(
                    "flex flex-row w-full relative",
                    "bg-[#F8FAFF] outline outline-background rounded-lg",
                  )}

                  style={{
                    borderRadius: `${6 * scale}px`,
                    outlineWidth: `${2 * scale}px`
                  }}
                >
                  <div
                    className={cn(
                      "flex flex-1 bg-background/90 backdrop-blur-md relative cursor-pointer",
                      "outline outline-offset-0 outline-transparent hover:outline-primary",
                      (activedIndex || 0) === index && "outline-primary",
                      "shadow-[8px_0px_30px_0px_rgba(44,108,255,0.07)]",
                      "transition-all	duration-500",
                    )}
                    style={{
                      borderRadius: `${6 * scale}px`,
                      padding: `${20 * scale}px ${38 * scale}px ${20 * scale}px ${28 * scale}px`,
                      outlineWidth: `${3 * scale}px`
                    }}
                  >
                    <div className="absolute right-0 top-0">
                      <PriceBadge size={12} label={desc} badge={badge as BadgeType} scale={scale} />
                    </div>
                    <div className="text-primary font-semibold align-baseline"
                      style={{
                        minWidth: `${140 * scale}px`
                      }}
                    >
                      <span
                        style={{
                          fontSize: `${20 * scale}px`,
                          lineHeight: `${28 * scale}px`
                        }}
                      >￥</span>
                      <span
                        className="font-semibold"
                        style={{
                          fontSize: `${32 * scale}px`,
                        }}
                      >{price}</span>
                    </div>
                    <div
                      className="flex flex-1 font-semibold items-top justify-end"
                      style={{
                        gap: `${4 * scale}px`,
                      }}
                    >
                      <span
                        className="font-semibold"
                        style={{
                          fontSize: `${32 * scale}px`,
                        }}
                      >{coins}</span>
                      <span
                        className="flex items-end"
                        style={{
                          fontSize: `${16 * scale}px`,
                          lineHeight: `${24 * scale}px`,
                          height: `${40 * scale}px`,
                        }}
                      >飞豆</span>
                      <span
                        className="flex items-end"
                        style={{
                          height: `${36 * scale}px`,
                        }}
                      >
                        <TokenIcon className="" size={16 * scale} />
                      </span>
                    </div>
                  </div>
                  <div
                    className={cn(
                      "flex items-center justify-end",
                    )}
                    style={{
                      width: `${180 * scale}px`,
                      paddingRight: `${28 * scale}px`,
                    }}
                  >
                    <div className={cn(
                      "flex items-center justify-center",
                      "text-annotation-foreground font-semibold"
                    )}
                      style={{
                        fontSize: `${18 * scale}px`,
                        lineHeight: `${28 * scale}px`,
                        marginTop: `${12}px`,
                        gap: `${4 * scale}px`,
                      }}
                    >
                      <span>{extraLabel ? extraLabel : `${extraCoins} 飞豆`}</span>
                      <TokenIcon size={16 * scale} />
                    </div>
                    <div
                      className={cn(
                        "absolute top-0 right-0",
                        "text-muted-foreground",
                        "bg-[linear-gradient(206.95deg,_rgba(229,232,248,1),_rgba(237,239,248,1)_100%)]",
                        "w-fit flex items-center justify-center",
                        "rounded-bl-lg rounded-tr-lg"
                      )}
                      style={{
                        fontSize: `${12 * scale}px`,
                        lineHeight: `${16 * scale}px`,
                        paddingTop: `${4 * scale}px`,
                        paddingBottom: `${4 * scale}px`,
                        paddingLeft: `${12 * scale}px`,
                        paddingRight: `${12 * scale}px`,
                      }}
                    >
                      {isVip ? '原价' : vip.vipDesc}
                    </div>
                  </div>
                </div>
              )
            })
          }
        </div>
      </div>
    </>
  );
}
